<template>
  <div
    id="teams-view"
    class="pa-4"
  >
    <view-page-header :show-team-select="false" />
    <v-divider class="mb-6" />
    <section>
      <v-row
        justify="center"
      >
        <v-col
          xl="3"
          lg="5"
          md="8"
        >
          <v-card>
            <v-card-title class="d-flex justify-space-between">
              <div class="subtitle-2">
                {{ $t('card_title.connected_fieldwiz') }}
              </div>
              <v-btn
                v-if="fieldwizOrganisations.length === 0"
                text
                color="primary"
                class="text-none"
                @click="openDialog()"
              >
                {{ $t('connect_fieldwiz.button') }}
                <v-icon
                  right
                  x-small
                >
                  far fa-plus
                </v-icon>
              </v-btn>
            </v-card-title>
            <v-divider />
            <v-card-text>
              <v-list
                v-if="fieldwizOrganisations.length > 0"
                class="v-list--bordered pa-0"
              >
                <template
                  v-for="organisation in fieldwizOrganisations"
                >
                  <v-list-item
                    :key="'fieldwiz'+organisation"
                  >
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ organisation.organisation_name }}
                      </v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn
                        icon
                        small
                        text
                        color="error"
                        @click="deleteIntegrationId = organisation.fieldwiz_integration_id; deleteConfirmationDialogVisible = true"
                      >
                        <v-icon small>
                          far fa-times
                        </v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </template>
              </v-list>
              <div
                v-else
                class="grey--text text-body-1 pa-6"
              >
                {{ $t('data_empty.connected_accounts') }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </section>

    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      :title="$t('destructive_action.label')"
      :text="$t('delete_confirm.fieldwiz_disconnect')"
      @confirmed="deleteIntegration()"
    />

    <card-dialog
      v-model="addDialog.visible"
      max-width="450px"
      :title="$t('card_dialog.connect_fieldwiz.title')"
    >
      <v-card-text
        class="pa-6"
      >
        <validation-observer ref="validation">
          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-text-field
              v-model="addDialog.username"
              :error-messages="errors"
              :label="$t('text_input.user.username.title')"
              outlined
            />
          </validation-provider>

          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-text-field
              v-model="addDialog.password"
              :error-messages="errors"
              :label="$t('text_input.login.password.title')"
              outlined
            />
          </validation-provider>
        </validation-observer>
      </v-card-text>
      <template v-slot:actions>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            outlined
            color="grey darken-2"
            class="text-none"
            @click="addDialog.visible = false"
          >
            {{ $t('button.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="addDialog.submitting"
            class="text-none"
            @click="submitIntegration()"
          >
            {{ $t('add_fieldwiz_account.button') }}
          </v-btn>
        </v-card-actions>
      </template>
    </card-dialog>
  </div>
</template>

<script>
import ViewPageHeader from '@/components/ViewPageHeader'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import { AuthApiService } from '@/data/services'
import CardDialog from '@/components/helpers/CardDialog.vue'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog.vue'

export default {
  name: 'AdminFieldwizView',
  components: {
    DeleteConfirmationDialog,
    CardDialog,
    ViewPageHeader
  },
  mixins: [deleteConfirmationMixin],
  data() {
    return {
      deleteIntegrationId: null,
      deleting: false,
      addDialog: {
        visible: false,
        username: '',
        password: '',
        submitting: false
      }
    }
  },
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    fieldwizIntegration() {
      return this.user.organisation_settings.integrations.find(i => i.key === 'fieldwiz')
    },
    fieldwizOrganisations() {
      return this.fieldwizIntegration.organisations === undefined ? [] : this.fieldwizIntegration.organisations
    }
  },
  watch: {},
  created() {
    if (this.$route.query.success) {
      this.$store.dispatch(this.$storeActions.ALERT, {
        text: this.$t('alert_connected_fieldwiz.success'),
        color: 'success'
      })

      this.$router.replace({ query: {} })
    }
  },
  methods: {
    openDialog() {
      this.addDialog.visible = true
    },
    async submitIntegration() {
      const validation = await this.$refs.validation.validate()

      if (!validation) return

      this.addDialog.submitting = true

      try {
        await AuthApiService.addFieldwizIntegration({
          username: this.addDialog.username,
          password: this.addDialog.password
        })

        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'success',
          text: this.$t('alert.fieldwiz_integration_added.success')
        })

        const authResponse = await AuthApiService.me()

        this.$store.dispatch(this.$storeActions.AUTH_LOGIN, authResponse.data)

        this.addDialog.visible = false
        this.addDialog.username = ''
        this.addDialog.password = ''
      } catch (e) {
        if (e?.response?.status === 400) {
          this.$store.dispatch(this.$storeActions.ALERT, {
            color: 'error',
            text: this.$t('alert.wrong_username_password')
          })

          return
        }

        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'error',
          text: this.$t('alert.fieldwiz_integration_connect.error')
        })
        throw e
      } finally {
        this.addDialog.submitting = false
      }
    },
    async deleteIntegration() {
      if (!this.deleteIntegrationId) return

      this.deleting = true

      try {
        await AuthApiService.deleteFieldwizIntegration(this.deleteIntegrationId)

        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'success',
          text: this.$t('alert.integration_removed.success')
        })

        this.deleteIntegrationId = null
        this.deleteConfirmationDialogVisible = false

        const authResponse = await AuthApiService.me()

        this.$store.dispatch(this.$storeActions.AUTH_LOGIN, authResponse.data)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'error',
          text: this.$t('alert.removing_integration.error')
        })
        throw e
      } finally {
        this.deleting = false
      }
    }
  }
}
</script>
